/**
 * Registro dos recursos do cadastro. A chave casa com `:recurso` na URL
 * (`/cadastro/pessoas`). O menu lateral e as rotas derivam desta lista — adicionar
 * uma entidade é adicionar um descriptor aqui, nada mais.
 */
import {
  emailAux,
  enderecoAux,
  redeSocialAux,
  telefoneAux,
} from '@/lib/recursos/auxiliares';
import { carteira } from '@/lib/recursos/carteira';
import { chip } from '@/lib/recursos/chip';
import { condominio } from '@/lib/recursos/condominio';
import { empresa } from '@/lib/recursos/empresa';
import { imovel } from '@/lib/recursos/imovel';
import { pessoa } from '@/lib/recursos/pessoa';
import { processo } from '@/lib/recursos/processo';
import { rastreador } from '@/lib/recursos/rastreador';
import { transacao } from '@/lib/recursos/transacao';
import { veiculo } from '@/lib/recursos/veiculo';
import type { DescritorRecurso } from '@/lib/recursos/tipos';

// A ordem aqui é a ordem no menu lateral (derivado de RECURSOS).
export const RECURSOS: Record<string, DescritorRecurso> = {
  pessoas: pessoa,
  empresas: empresa,
  condominios: condominio,
  imoveis: imovel,
  veiculos: veiculo,
  transacoes: transacao,
  carteiras: carteira,
  processos: processo,
  rastreadores: rastreador,
  // --- Auxiliares: meio, não fim. Agrupados no menu por `grupo`. ---
  chips: chip,
  'aux-telefones': telefoneAux,
  'aux-emails': emailAux,
  'aux-enderecos': enderecoAux,
  'aux-redes': redeSocialAux,
};

export const listaRecursos = (): DescritorRecurso[] => Object.values(RECURSOS);

export const obterRecurso = (chave?: string): DescritorRecurso | undefined =>
  chave ? RECURSOS[chave] : undefined;

/**
 * `ownerTipo` (singular, como o backend fala) → chave de rota (plural, como a URL fala).
 *
 * DERIVADO do registro, nunca escrito à mão: cada descriptor já declara o seu `ownerTipo`, e um
 * mapa paralelo divergiria no dia em que alguém acrescentasse uma entidade e esquecesse a segunda
 * lista. Todo resultado heterogêneo (busca, dossiê) precisa disto para montar um link.
 */
export const ROTA_POR_OWNER: Record<string, string> = Object.fromEntries(
  Object.entries(RECURSOS)
    .filter(([, d]) => d.ownerTipo)
    .map(([chave, d]) => [d.ownerTipo as string, chave]),
);

/** Caminho da lista (ou da edição, com `id`) a partir do tipo que o backend devolveu. */
export const rotaDe = (ownerTipo: string, id?: string): string => {
  const chave = ROTA_POR_OWNER[ownerTipo];
  if (!chave) return '/';
  return id ? `/cadastro/${chave}/${id}` : `/cadastro/${chave}`;
};

/** Caminho da tela de leitura (dossiê + grafo) de uma entidade. */
export const rotaEntidade = (ownerTipo: string, id: string): string =>
  `/entidade/${ownerTipo}/${id}`;
